import { TabContext, TabList, TabPanel } from '@mui/lab';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Grid,
  Paper,
  Tab,
  Typography,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import { useSnackbar } from 'notistack';

import { useEffect, useState } from 'react';

import { SubmitHandler } from 'react-hook-form';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useLocation, useNavigate, useParams } from 'react-router';

import { Link } from 'react-router-dom';

import { AssetChildList, AssetForm } from '.';

import * as assetApi from '../../api/app/assetApi';

import * as commonApi from '../../api/app/commonApi';

import { ApiError } from '../../components/core/ApiError/ApiError';

import { Loader } from '../../components/core/Loader/Loader';

import { PageContent } from '../../components/core/PageContent/PageContent';

import { useAuth } from '../../contexts/authWindows';

import { useTranslation } from '../../contexts/translation';

import {
  Asset,
  AssetDetailTabValues,
  AssetInputs,
  AssetMainDashboardSetting,
  AssetTreeView,
  MonitoredPanelValues,
} from '../../types/app/asset.types';

import { Shift } from '../../types/app/shifts.types';

import { UserRoles } from '../../types/core/auth.types';

import { dictionary } from '../../utils/core/dictionary';

import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/asset.functions';

import { paths } from '../../utils/core/routes';

import { DashboardList } from '../Dashboard/index';

import { MainDashboardDetail } from '../MainDashboard/MainDashboardDetail';

import { MonitoredCounterList } from '../MonitoredCounter';

import { MonitoredCounterScrapList } from '../MonitoredCounterScrap';

import { MonitoredCycleTimeList } from '../MonitoredCycleTime';

import { MonitoredProcessDataList } from '../MonitoredProcessData';

import { MonitoredStateList } from '../MonitoredState';

import { PerformanceCommentCategoryList } from '../PerformanceCommentCategory';

import { ShiftToAssetList } from '../Shift/ShiftToAssetList';

import { useStyles } from './AssetForm.styles';

export function AssetDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useAuth();
  const { id } = useParams();
  const { state } = useLocation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isReadOnly = user?.roles.includes(UserRoles.Admin) ? false : true;
  const navigate = useNavigate();
  const [defaultValues, setDefaultValues] = useState<AssetInputs | null>(null);
  const [assetFullPath, setAssetFullPath] = useState<Asset[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { data: isUsedHydra } = useQuery<boolean>('useHydra', commonApi.useHydraDataService);

  const {
    data: shiftList,
    isLoading: shiftIsLoading,
    isError: shiftIsError,
  } = useQuery<Shift[]>(['shift', id], () => assetApi.getShiftsForAsset(Number(id)));

  const { data: assetTreeViewData } = useQuery<AssetTreeView>(['assetTreeView'], () =>
    assetApi.assetGetTreeView(Number(id)),
  );

  const [value, setValue] = useState<string>(
    state && (state as any).tabValue
      ? (state as any).tabValue.toString()
      : AssetDetailTabValues.BASIC,
  );
  const [expanded, setExpanded] = useState(
    state && (state as any).panelValue
      ? (state as any).panelValue.toString()
      : MonitoredPanelValues.MONITORED_COUNTER,
  );

  useEffect(() => {
    if (!state) return;
    if ((state as any).tabValue) setValue((state as any).tabValue);
    if ((state as any).panelValue) setExpanded((state as any).panelValue);
  }, [state]);

  const handleChangeTab = (event: any, newValue: string) => {
    setValue(newValue);
  };
  const handleChange = (panel: any) => (event: any, newExpanded: any) => {
    setExpanded(newExpanded ? panel : false);
  };

  const { mutate: mutateUpdateAsset } = useMutation(assetApi.updateAsset);
  const {
    data: assetData,
    isLoading,
    isError,
  } = useQuery<Asset>(['asset', id], () => assetApi.getAsset(Number(id)));

  const { data: mainDashboardSetting } = useQuery<AssetMainDashboardSetting>(
    ['dashboardConfig'],
    () => assetApi.getMainDashBoardSettingForAsset(Number(id)),
    {
      enabled: assetData && assetData.useWithDashboards,
    },
  );

  useEffect(() => {
    if (!assetData) return;
    setDefaultValues({
      parentAssetId: assetData.parentAssetId,
      assetType: assetData.assetType,
      groupId: assetData.groupId,
      name: assetData.name,
      influxMachineName: assetData.influxMachineName,
      useWithDashboards: assetData.useWithDashboards,
      grafanaFolderUId: assetData.grafanaFolderUId,
      hydraMachineNumber: assetData.hydraMachineNumber,
      loadPlanFromHydra: assetData.loadPlanFromHydra,
      loadPlanFromHydraMultiplicity: assetData.loadPlanFromHydraMultiplicity,
      loadPlanFromHydraMultiple: assetData.loadPlanFromHydraMultiple,
      unitOverride: assetData.unitOverride,
    });
  }, [assetData]);

  useEffect(() => {
    if (!assetData) return;

    const getFullPath = async (asset: Asset) => {
      let assetsFull: Asset[] = [asset];
      if (asset.parentAssetId) {
        const parent = await assetApi.getAsset(asset.parentAssetId);
        const resultParent = await getFullPath(parent);
        assetsFull = [...resultParent, ...assetsFull];
      }
      return assetsFull;
    };
    const result = getFullPath(assetData);
    result.then((x) => setAssetFullPath(x));
  }, [assetData]);

  const addSeparator = (text: string) => {
    if (text.length > 0) return ', ';
    return '';
  };

  useEffect(() => {
    if (!assetTreeViewData || !assetData || !shiftList || !mainDashboardSetting) return;
    let _errorMessage = '';
    assetData.useWithDashboards && shiftList.length < 1
      ? (_errorMessage += addSeparator(_errorMessage) + t('page.asset.error-message-1'))
      : '';
    assetData.useWithDashboards && assetTreeViewData.counters.length < 1
      ? (_errorMessage += addSeparator(_errorMessage) + t('page.asset.error-message-2'))
      : '';
    assetData.useWithDashboards &&
    mainDashboardSetting.showScrap &&
    assetTreeViewData.countersScrap.length < 1
      ? (_errorMessage += addSeparator(_errorMessage) + t('page.asset.error-message-3'))
      : '';
    assetData.useWithDashboards &&
    mainDashboardSetting.showActualCycleTime &&
    assetTreeViewData.cycleTimes.length < 1
      ? (_errorMessage += addSeparator(_errorMessage) + t('page.asset.error-message-4'))
      : '';
    assetData.useWithDashboards &&
    mainDashboardSetting.showActualState &&
    assetTreeViewData.states.length < 1
      ? (_errorMessage += addSeparator(_errorMessage) + t('page.asset.error-message-5'))
      : '';
    setErrorMessage(_errorMessage);
  }, [assetTreeViewData, assetData, shiftList, mainDashboardSetting]);

  const handleOnSubmit: SubmitHandler<AssetInputs> = (data: AssetInputs) => {
    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
    });

    if (data.loadPlanFromHydraMultiple) {
      requestData.loadPlanFromHydraMultiple = parseFloat(
        data.loadPlanFromHydraMultiple?.toString().replace(',', '.'),
      );
    }

    mutateUpdateAsset(
      { ...requestData },
      {
        onSuccess: (asset: Asset) => {
          enqueueSnackbar(t('page.asset.item-edited'), { variant: 'success' });

          queryClient.invalidateQueries(['asset']);
          navigate(`${paths.ASSETS_DETAIL}/${asset.id}`);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError || shiftIsError) return <ApiError />;
  if (
    isLoading ||
    shiftIsLoading ||
    !defaultValues ||
    !assetData ||
    !shiftList ||
    !assetFullPath ||
    isUsedHydra === undefined
  )
    return <Loader />;

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb">
        {assetFullPath.map((asset: any, index: number) => {
          return assetFullPath.length - 1 === index ? (
            <Typography
              key={index}
              component={'h4'}
              variant={'h4'}
              sx={{
                color: theme.palette.primary.dark,
                textDecoration: 'none',
              }}
            >
              {asset.name}
            </Typography>
          ) : (
            <Link
              key={asset.id}
              to={`${paths.ASSETS_DETAIL}/${asset.id}`}
              state={{
                tabValue: AssetDetailTabValues.BASIC,
              }}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textDecoration: 'underline',
              }}
            >
              <Typography component={'h4'} variant={'h4'}>
                {asset.name}
              </Typography>
            </Link>
          );
        })}
      </Breadcrumbs>
      <PageContent>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        <Box sx={{ width: 'auto', typography: 'body1', mt: 5 }}>
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handleChangeTab}
                aria-label="lab API tabs"
                style={{ overflow: 'auto' }}
                variant="scrollable"
              >
                <Tab label={t('page.asset.detail-tabs.tab-1')} value={AssetDetailTabValues.BASIC} />
                {assetData?.useWithDashboards && (
                  <Tab
                    label={t('page.asset.detail-tabs.tab-2')}
                    value={AssetDetailTabValues.MAIN_DASHBOARD}
                  />
                )}
                <Tab
                  label={t('page.asset.detail-tabs.tab-3')}
                  value={AssetDetailTabValues.MONITORED_VALUES}
                />
                {assetData?.useWithDashboards && (
                  <Tab
                    label={t('page.asset.detail-tabs.tab-4')}
                    value={AssetDetailTabValues.DASHBOARD}
                  />
                )}
                {assetData?.useWithDashboards && (
                  <Tab
                    label={dictionary.PERFORMANCE_COMMENT_CATEGORY}
                    value={AssetDetailTabValues.PERFORMANCE_COMMENT_CATEGORY}
                  />
                )}
              </TabList>
            </Box>
            <TabPanel value={AssetDetailTabValues.BASIC}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={8} lg={4} mt={6}>
                  <Paper className={classes.paper}>
                    <AssetForm
                      onSubmit={handleOnSubmit}
                      isUsedHydra={isUsedHydra}
                      onBack={() =>
                        navigate(
                          assetData && assetData.parentAssetId
                            ? `${paths.ASSETS_DETAIL}/${assetData.parentAssetId}`
                            : paths.ASSETS_LIST,
                        )
                      }
                      defaultValues={defaultValues}
                      isReadOnly={isReadOnly}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8} lg={8}>
                  {assetData && <AssetChildList pathList={assetFullPath} isReadOnly={isReadOnly} />}
                </Grid>
                {assetData && assetData?.useWithDashboards && (
                  <Grid item xs={12}>
                    <ShiftToAssetList isReadOnly={isReadOnly} />
                  </Grid>
                )}
              </Grid>
            </TabPanel>
            <TabPanel value={AssetDetailTabValues.MAIN_DASHBOARD}>
              <MainDashboardDetail />
            </TabPanel>
            <TabPanel value={AssetDetailTabValues.MONITORED_VALUES}>
              <Accordion
                expanded={expanded === MonitoredPanelValues.MONITORED_COUNTER}
                onChange={handleChange(MonitoredPanelValues.MONITORED_COUNTER)}
              >
                <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                  <Typography variant="h6">{dictionary.MONITORED_COUNTER}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MonitoredCounterList parentId={Number(id)} />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === MonitoredPanelValues.MONITORED_COUNTER_SCRAP}
                onChange={handleChange(MonitoredPanelValues.MONITORED_COUNTER_SCRAP)}
              >
                <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
                  <Typography variant="h6">{dictionary.MONITORED_COUNTER_SCRAP}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MonitoredCounterScrapList parentId={Number(id)} />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === MonitoredPanelValues.MONITORED_CYCLE}
                onChange={handleChange(MonitoredPanelValues.MONITORED_CYCLE)}
              >
                <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
                  <Typography variant="h6">{dictionary.MONITORED_CYCLE_TIME}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MonitoredCycleTimeList parentId={Number(id)} />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === MonitoredPanelValues.MONITORED_PROCESS_DATA}
                onChange={handleChange(MonitoredPanelValues.MONITORED_PROCESS_DATA)}
              >
                <AccordionSummary aria-controls="panel4d-content" id="panel4d-header">
                  <Typography variant="h6">{dictionary.MONITORED_PROCESS_DATA}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MonitoredProcessDataList parentId={Number(id)} />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={expanded === MonitoredPanelValues.MONITORED_STATE}
                onChange={handleChange(MonitoredPanelValues.MONITORED_STATE)}
              >
                <AccordionSummary aria-controls="panel5d-content" id="panel5d-header">
                  <Typography variant="h6">{dictionary.MONITORED_STATE}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MonitoredStateList parentId={Number(id)} />
                </AccordionDetails>
              </Accordion>
            </TabPanel>
            <TabPanel value={AssetDetailTabValues.DASHBOARD}>
              <DashboardList parentId={Number(id)} isReadOnly={isReadOnly} />
            </TabPanel>
            <TabPanel value={AssetDetailTabValues.PERFORMANCE_COMMENT_CATEGORY}>
              <PerformanceCommentCategoryList parentId={Number(id)} isReadOnly={isReadOnly} />
            </TabPanel>
          </TabContext>
        </Box>
      </PageContent>
    </>
  );
}
